import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useVertical } from '../context/VerticalContext';
import { CalendarClock, Car, ChevronRight, Loader2, BellOff, Bell, Mail, AlertTriangle } from 'lucide-react';

interface UpcomingMot {
    vehicle_id: string;
    vrm: string;
    make: string | null;
    model: string | null;
    vehicle_year: number | null;
    mot_expiry: string;
    days_until: number;
    customer_id: string | null;
    customer_name: string | null;
    customer_email: string | null;
    mot_reminder_enabled: boolean;
    last_reminder_sent_at: string | null;
}

const WINDOWS = [30, 60, 90];

function fmtDate(d: string) {
    return new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

function dueBadge(days: number): { label: string; cls: string } {
    if (days < 0) return { label: `Overdue by ${Math.abs(days)} day${Math.abs(days) === 1 ? '' : 's'}`, cls: 'bg-danger/15 text-danger border-danger/30' };
    if (days === 0) return { label: 'Due today', cls: 'bg-danger/15 text-danger border-danger/30' };
    if (days <= 7) return { label: `Due in ${days} day${days === 1 ? '' : 's'}`, cls: 'bg-danger/10 text-danger border-danger/25' };
    if (days <= 30) return { label: `Due in ${days} days`, cls: 'bg-gold-fill/10 text-gold border-gold/25' };
    return { label: `Due in ${days} days`, cls: 'bg-tint/5 text-fg-3 border-tint/10' };
}

export default function MotRemindersPage() {
    const { activeOrgId } = useVertical();
    const navigate = useNavigate();
    const [rows, setRows] = useState<UpcomingMot[]>([]);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [windowDays, setWindowDays] = useState(60);

    const fetchRows = useCallback(async () => {
        if (!activeOrgId) return;
        setLoading(true);
        setLoadError(null);
        try {
            const { data, error } = await supabase.rpc('fn_get_upcoming_mots', {
                p_org_id: activeOrgId,
                p_within_days: windowDays,
            });
            if (error) throw error;
            setRows((data ?? []) as UpcomingMot[]);
        } catch (err) {
            console.error('Failed to load upcoming MOTs:', err);
            setRows([]);
            // An empty list and a failed load look identical to the user, and
            // "no MOTs due" is a dangerous thing to say when we simply couldn't
            // ask. PGRST202 = the RPC isn't deployed on this environment.
            const code = (err as { code?: string } | null)?.code;
            setLoadError(
                code === 'PGRST202'
                    ? "MOT reminders aren't available on this environment yet."
                    : "Couldn't load upcoming MOTs. Try again in a moment."
            );
        } finally {
            setLoading(false);
        }
    }, [activeOrgId, windowDays]);

    useEffect(() => { fetchRows(); }, [fetchRows]);

    const overdueCount = rows.filter((r) => r.days_until < 0).length;

    return (
        <div className="w-full animate-in fade-in slide-in-from-bottom-4">
            {/* Header */}
            <div className="flex items-start justify-between gap-4 mb-6">
                <div>
                    <h1 className="text-3xl font-bold text-fg flex items-center gap-2.5">
                        <CalendarClock className="w-7 h-7 text-accent" />
                        MOT Reminders
                    </h1>
                    <p className="text-sm text-fg-3 mt-1">
                        Vehicles with an MOT due soon. Click a row to open the customer and send a reminder.
                    </p>
                </div>
                <select
                    value={windowDays}
                    onChange={(e) => setWindowDays(Number(e.target.value))}
                    className="bg-tint/5 border border-tint/10 rounded-lg px-3 py-2 text-sm text-fg outline-none focus:ring-2 focus:ring-accent"
                >
                    {WINDOWS.map((w) => (
                        <option key={w} value={w} className="bg-card">Next {w} days</option>
                    ))}
                </select>
            </div>

            {overdueCount > 0 && (
                <div className="mb-4 flex items-center gap-2 p-3 rounded-lg bg-danger/10 border border-danger/20 text-danger text-sm">
                    <AlertTriangle className="w-4 h-4 shrink-0" />
                    {overdueCount} vehicle{overdueCount === 1 ? '' : 's'} already overdue.
                </div>
            )}

            {loading ? (
                <div className="flex items-center justify-center py-16">
                    <Loader2 className="w-6 h-6 text-accent animate-spin" />
                </div>
            ) : loadError ? (
                <div className="text-center py-16 text-fg-4">
                    <AlertTriangle className="w-10 h-10 mx-auto mb-3 text-gold/60" />
                    <p className="text-sm text-fg-2">{loadError}</p>
                    <button
                        type="button"
                        onClick={fetchRows}
                        className="mt-4 px-3 py-1.5 text-xs font-semibold rounded-lg bg-tint/5 border border-tint/10 text-fg-2 hover:bg-tint/10 transition-colors"
                    >
                        Retry
                    </button>
                </div>
            ) : rows.length === 0 ? (
                <div className="text-center py-16 text-fg-4">
                    <Car className="w-10 h-10 mx-auto mb-3 opacity-40" />
                    <p className="text-sm">No MOTs due in the next {windowDays} days.</p>
                    <p className="text-xs mt-1">Add an MOT due date on a vehicle (customer profile) and it'll show here.</p>
                </div>
            ) : (
                <div className="space-y-2">
                    {rows.map((r) => {
                        const badge = dueBadge(r.days_until);
                        const vehicleName = [r.vehicle_year, r.make, r.model].filter(Boolean).join(' ');
                        const clickable = !!r.customer_id;
                        return (
                            <button
                                key={r.vehicle_id}
                                type="button"
                                disabled={!clickable}
                                onClick={() => clickable && navigate(`/clients/${r.customer_id}`)}
                                className={`w-full text-left bg-panel border border-line rounded-xl p-4 flex items-center gap-4 transition-colors ${clickable ? 'hover:bg-tint/5 cursor-pointer' : 'opacity-70 cursor-default'}`}
                            >
                                <div className="w-10 h-10 rounded-lg bg-tint/5 flex items-center justify-center text-fg-3 shrink-0">
                                    <Car className="w-5 h-5" />
                                </div>

                                <div className="min-w-0 flex-1">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <span className="font-mono text-xs bg-raised text-fg-2 px-1.5 py-0.5 rounded border border-tint/10">{r.vrm}</span>
                                        <span className="text-sm text-fg-2 truncate">{vehicleName || 'Vehicle'}</span>
                                    </div>
                                    <p className="text-sm font-semibold text-fg mt-0.5 truncate">
                                        {r.customer_name || <span className="text-fg-4 font-normal">No customer linked</span>}
                                    </p>
                                </div>

                                <div className="text-right shrink-0 flex flex-col items-end gap-1">
                                    <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full border ${badge.cls}`}>{badge.label}</span>
                                    <span className="text-xs text-fg-4">{fmtDate(r.mot_expiry)}</span>
                                    <span className="flex items-center gap-2 mt-0.5">
                                        {r.last_reminder_sent_at ? (
                                            <span className="flex items-center gap-1 text-[10px] text-success"><Mail className="w-3 h-3" />Reminded</span>
                                        ) : null}
                                        {r.mot_reminder_enabled
                                            ? <Bell className="w-3.5 h-3.5 text-fg-4" aria-label="Reminders on" />
                                            : <BellOff className="w-3.5 h-3.5 text-fg-4" aria-label="Reminders off" />}
                                    </span>
                                </div>

                                <ChevronRight className={`w-5 h-5 shrink-0 ${clickable ? 'text-fg-4' : 'text-transparent'}`} />
                            </button>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
